"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { domAnimation, LazyMotion, m } from "framer-motion";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { MagneticButton } from "@/components/custom/magnetic_button";
import { Squares } from "@/components/custom/squares_background";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { type FormSchemaType, formSchema } from "@/lib/form_schema";
import { submitForm } from "@/lib/form_submit";
import { buildExpertWhatsAppHref } from "@/lib/pricing-region";

const contactFormSchema = formSchema.extend({
	Name: z.string().trim().min(1, "Name is required"),
	Message: z.string().trim().min(1, "Message is required"),
});

type ContactFormSchemaType = z.infer<typeof contactFormSchema>;

const CONTACT_WHATSAPP_HREF = buildExpertWhatsAppHref("usd");

export default function ContactClient() {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ContactFormSchemaType>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			Name: "",
			Email: "",
			Message: "",
		},
	});

	const onSubmit = async (data: ContactFormSchemaType) => {
		setIsSubmitting(true);
		try {
			const result = await submitForm(data satisfies FormSchemaType);

			if (result.success) {
				toast.success("Thank you for your message! We'll be in touch soon.");
				reset();
			} else {
				toast.error(result.error || "Something went wrong. Please try again.");
			}
		} catch (error) {
			toast.error("Failed to submit form. Please try again later.");
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<LazyMotion features={domAnimation}>
			<div
				className="contact-page min-h-screen"
				style={{ backgroundColor: "#080808" }}
			>
				{/* Creative Hero Section - extends behind navbar */}
				<section
					className="relative h-[60vh] md:min-h-[800px] flex items-center justify-center overflow-hidden text-white -mt-20 pt-20"
					style={{ backgroundColor: "#080808" }}
				>
					<div className="absolute inset-0 z-0">
						<div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/20 rounded-full blur-[150px] animate-pulse" />
						<div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]" />
						<Squares
							theme="dark"
							direction="diagonal"
							speed={0.2}
							squareSize={50}
							className="opacity-20"
							hoverFillColor="#ef4444"
						/>
					</div>

					<div className="container mx-auto px-4 relative z-10 text-center">
						<m.div
							initial={{ opacity: 0, y: 50 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8 }}
						>
							<h1 className="font-syne font-black uppercase leading-[0.85] tracking-tighter">
								<span className="block text-[12vw] md:text-[8rem] text-gray-50/3 stroke-text text-stroke-2 text-stroke-zinc-500 hover:text-white/10 transition-colors duration-500">
									Want
								</span>
								<span
									className="block text-[12vw] md:text-[4rem] text-white relative"
									style={{ fontFamily: "Inter", fontWeight: 600 }}
								>
									Special Pricing?
									<m.div
										className="absolute top-1/2 -right-12 md:-right-24 w-24 h-24 md:w-32 md:h-32 bg-red-600 rounded-full blur-3xl -z-10"
										animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
										transition={{ repeat: Infinity, duration: 4 }}
									/>
								</span>
							</h1>
						</m.div>

						<m.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.5, duration: 0.8 }}
							className="mt-12 max-w-2xl mx-auto"
						>
							<p className="text-zinc-400 text-lg md:text-2xl font-inter leading-relaxed mb-6">
								Ready to start your ascent? Reach out and let's build your
								channel's future together.
							</p>
							<p className="text-zinc-500 text-base md:text-lg font-inter leading-relaxed">
								Whether you need custom pricing for large-scale campaigns, bulk
								order discounts, or partnership opportunities, our team is here
								to help. With years of experience running video ad campaigns for
								creators across every niche — we know what it takes to help your
								content find its people. Expect personalized solutions tailored
								to your content niche and goals.
							</p>
						</m.div>
					</div>
				</section>

				{/* Contact Form and Info Section */}
				<section
					className="py-20 relative bg-[#080808]"
					style={{ backgroundColor: "#080808" }}
				>
					<div className="container mx-auto px-4">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
							{/* Form Column */}
							<m.div
								initial={{ opacity: 0, y: 50 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.6 }}
							>
								<h2 className="font-syne text-4xl font-bold uppercase tracking-tight mb-3 text-white">
									Send a Message
								</h2>
								<p className="font-inter text-zinc-400 mb-10">
									Typically responds within an hour
								</p>

								<div className="bg-zinc-900/80 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl p-8 md:p-10 relative overflow-hidden group hover:border-white/20 transition-colors duration-500">
									{/* Decorative gradient blob */}
									<div className="absolute -top-20 -right-20 w-40 h-40 bg-red-600/10 rounded-full blur-3xl group-hover:bg-red-600/20 transition-colors duration-500" />

									<form
										onSubmit={handleSubmit(onSubmit)}
										className="relative z-10 flex flex-col gap-5"
									>
										<div className="space-y-1.5">
											<Input
												type="text"
												placeholder="Name*"
												className="h-12 rounded-lg bg-black/50 border-white/10 text-white placeholder:text-zinc-500 focus:border-red-600 focus:bg-black transition-[background-color,border-color,box-shadow] font-inter shadow-inner focus:ring-1 focus:ring-red-600/50"
												{...register("Name")}
											/>
											{errors.Name && (
												<p className="text-red-500 text-xs font-medium ml-1">
													{errors.Name.message}
												</p>
											)}
										</div>

										<div className="space-y-1.5">
											<Input
												type="email"
												placeholder="Email*"
												className="h-12 rounded-lg bg-black/50 border-white/10 text-white placeholder:text-zinc-500 focus:border-red-600 focus:bg-black transition-[background-color,border-color,box-shadow] font-inter shadow-inner focus:ring-1 focus:ring-red-600/50"
												{...register("Email")}
											/>
											{errors.Email && (
												<p className="text-red-500 text-xs font-medium ml-1">
													{errors.Email.message}
												</p>
											)}
										</div>

										<div className="space-y-1.5">
											<Textarea
												placeholder="Tell us about your project or campaign goals*"
												className="min-h-[150px] rounded-lg bg-black/50 border-white/10 text-white placeholder:text-zinc-500 focus:border-red-600 focus:bg-black transition-[background-color,border-color,box-shadow] font-inter resize-none p-4 shadow-inner focus:ring-1 focus:ring-red-600/50"
												{...register("Message")}
											/>
											{errors.Message && (
												<p className="text-red-500 text-xs font-medium ml-1">
													{errors.Message.message}
												</p>
											)}
										</div>

										<MagneticButton
											type="submit"
											className="w-full mt-2"
											disabled={isSubmitting}
										>
											<div className="w-full h-14 bg-red-600 text-white rounded-lg font-syne font-bold uppercase tracking-wide flex items-center justify-center hover:bg-red-500 transition-colors shadow-lg shadow-red-900/40">
												{isSubmitting ? "Sending..." : "Send Message"}
											</div>
										</MagneticButton>
									</form>
								</div>
							</m.div>

							{/* Contact Info Column */}
							<m.div
								className="flex flex-col justify-center"
								initial={{ opacity: 0, x: 50 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.6, delay: 0.2 }}
							>
								<h2 className="font-syne text-4xl font-bold uppercase tracking-tight mb-3 text-white">
									Contact Info
								</h2>
								<p className="font-inter text-zinc-400 mb-12">
									We're available to assist you with any questions about our
									video ad campaign services.
								</p>

								<div className="space-y-10">
									<ContactItem
										icon={Phone}
										title="Phone"
										content={
											<div className="flex flex-col gap-1 text-zinc-300">
												<a
													href={CONTACT_WHATSAPP_HREF}
													target="_blank"
													rel="noreferrer"
													className="animate-shine-red inline-flex min-h-7 items-center font-bold text-white transition-colors hover:text-red-400"
													aria-label="WhatsApp TheHighraysYT"
												>
													WA: +1 4167264766
												</a>
												<a
													href="tel:+918273262260"
													className="inline-flex min-h-7 items-center transition-colors hover:text-red-400"
												>
													+91 8273262260 (India)
												</a>
											</div>
										}
										subContent="Mon-Fri: 9am-6pm (EST & IST)"
									/>
									<ContactItem
										icon={Mail}
										title="Email"
										content="info@thehighrays.com"
										subContent="We respond within an hour"
									/>
									<ContactItem
										icon={MapPin}
										title="Location"
										content={
											<div className="flex flex-col gap-1 text-zinc-300">
												<span className="font-semibold">Ottawa, Canada</span>
												<span className="text-sm">New Delhi, India</span>
											</div>
										}
									/>
									<ContactItem
										icon={Clock}
										title="Business Hours"
										content="Mon - Fri: 9:00 AM - 6:00 PM"
										subContent="Sat & Sun: 10:00 AM - 2:00 PM"
									/>
								</div>
							</m.div>
						</div>
					</div>
				</section>

				{/* FAQ Section */}
				<section className="py-24" style={{ backgroundColor: "#080808" }}>
					<div className="container mx-auto px-4 max-w-4xl">
						<h2 className="font-syne text-4xl md:text-5xl font-bold uppercase text-center mb-16 text-white tracking-tighter">
							Quick <span className="text-red-600">Answers</span>
						</h2>

						<Accordion type="single" collapsible className="w-full space-y-4">
							<FAQItem
								value="item-1"
								question="How fast will I see results?"
								answer="Most clients see views starting within 24-48 hours of campaign launch. Our automated systems work directly with Google Ads to approve and start serving your video ads as quickly as possible."
							/>
							<FAQItem
								value="item-2"
								question="Is this safe for my channel?"
								answer="Absolutely. Every campaign runs through official Google Ads, fully compliant with platform policies. Your channel stays safe. All traffic comes from real people discovering your content through paid advertising."
							/>
							<FAQItem
								value="item-3"
								question="How do I get started?"
								answer="Choose a package on our pricing page, share your video link, and we'll launch your campaign within 24 hours. You can track your campaign progress in real-time."
							/>
							<FAQItem
								value="item-4"
								question="Do you offer refunds?"
								answer="Yes, if your campaign is not approved by Google Ads or we're unable to deliver on the agreed scope, we will issue a full refund to your original payment method."
							/>
						</Accordion>
					</div>
				</section>
			</div>
		</LazyMotion>
	);
}

function ContactItem({
	icon: Icon,
	title,
	content,
	subContent,
}: {
	icon: React.ElementType;
	title: string;
	content: ReactNode;
	subContent?: string;
}) {
	return (
		<div className="flex items-start group">
			<div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center text-zinc-400 group-hover:text-red-600 group-hover:border-red-600/20 group-hover:bg-red-600/5 transition-colors duration-300 mr-6 shrink-0">
				<Icon className="w-6 h-6" />
			</div>
			<div>
				<h3 className="font-syne text-lg font-bold uppercase tracking-wide text-white mb-1">
					{title}
				</h3>
				<div className="font-inter text-zinc-300 font-medium">{content}</div>
				{subContent && (
					<p className="font-inter text-zinc-500 text-sm mt-1">{subContent}</p>
				)}
			</div>
		</div>
	);
}

function FAQItem({
	value,
	question,
	answer,
}: {
	value: string;
	question: string;
	answer: string;
}) {
	return (
		<AccordionItem
			value={value}
			className="border border-white/10 bg-zinc-900/50 rounded-2xl px-6 data-[state=open]:border-red-600/20 data-[state=open]:bg-zinc-900 data-[state=open]:shadow-lg transition-[background-color,border-color,box-shadow] duration-300"
		>
			<AccordionTrigger className="font-syne font-bold uppercase text-lg hover:no-underline py-6 text-left text-white data-[state=open]:text-red-600">
				{question}
			</AccordionTrigger>
			<AccordionContent className="font-inter text-zinc-400 text-base leading-relaxed pb-6">
				{answer}
			</AccordionContent>
		</AccordionItem>
	);
}
